import { useMemo } from 'react';
import _ from 'lodash';
import WrapperDataGrid from '@modules/dataGrid/WrapperDataGrid';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  generateGlobalColumns,
  generateBasicColumn,
  generateDateColumn,
  generateEntityColumn,
  generateMultiEntityColumn,
  generateNumberColumn,
  TInheritedProps,
  IDataGridConfig,
} from '@ts/storybook';
import {
  CCategory,
  IConfigComponent,
  CElement,
  CUser,
  EFlavors,
  CLocation,
  CEvent,
  CEventType,
  IExtraColumns,
} from '@ts/ui-models';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { useAppSelector } from '@redux/store';
import { onClickMultiEntityCell } from '../Flavors/Items/ItemColumns';

export const defaultConfigComponent: IConfigComponent = {
  componentId: 'events',
  aclId: 'taEvent',
  entity: 'event',
  fetchURL: '/api/v2/events',
  crudURL: '/api/v2/event',
  mainClass: CEvent,
};

interface EventsProps {
  tElement?: CElement;
  configComponent?: IConfigComponent;
  inheritedProps?: TInheritedProps<false>;
  title?: string;
  infoDataGrid?: string | any[];
  where?: any;
}

const Events = (props: EventsProps) => {
  const navigate = useNavigate();
  const { configComponent = defaultConfigComponent, tElement, inheritedProps } = props;
  const globalSettings = useAppSelector(state => state.global.settings);
  const componentSettingsEvents = globalSettings?.componentSettings[configComponent.componentId];

  const extraColumnFields: string[] = Array.isArray(componentSettingsEvents?.extraColumns)
    ? componentSettingsEvents!.extraColumns.map((column: IExtraColumns) => column.field).filter(Boolean)
    : [];

  const isLocation = tElement?.haveFlavor?.(EFlavors.LOCATION) ?? false;

  const config: IDataGridConfig = {
    columnVisibilityModel: {
      id: false,
      name: false,
      description: false,
      ...Object.fromEntries(extraColumnFields.map(f => [f, false])),
    },
    filterModel: {
      items: props.configComponent?.where
        ? []
        : [
            {
              id: 'minimumFilter',
              field: 'timestamp',
              value: dayjs().startOf('month').format('YYYY-MM-DDTHH:mm'),
              operator: 'after',
            },
          ],
    },
  };

  //#region Functions
  const generateExtraColumnsFromSettings = (): GridColDef<any>[] => {
    const extraColumns: GridColDef<any>[] = [];
    componentSettingsEvents?.extraColumns?.forEach(extraColumn => {
      switch (extraColumn.type) {
        case 'number':
          extraColumns.push(
            generateNumberColumn({ field: extraColumn.field, headerName: extraColumn.columnName }, { field: extraColumn.field })
          );
          break;
        case 'multiEntitySelector':
          extraColumns.push(
            generateMultiEntityColumn(
              { field: extraColumn.field, headerName: extraColumn.columnName },
              { field: extraColumn.field, mainClass: CElement },
              {
                inheritedProps,
                chips: { onlyModal: true },
                onClick: onClickMultiEntityCell,
              }
            )
          );
          break;
        default:
          extraColumns.push(
            generateBasicColumn({ field: extraColumn.field, headerName: extraColumn.columnName }, { field: extraColumn.field })
          );

          break;
      }
    });
    return extraColumns;
  };

  const getColumns = useMemo((): GridColDef<any>[] => {
    const mixProps = { ...props, configComponent, navigate };
    const globalCells = generateGlobalColumns(mixProps);
    return [
      globalCells.id(),
      generateDateColumn({ field: 'timestamp', headerName: 'Date', isFilterable: true }, { field: 'timestamp' }),
      globalCells.name(),
      generateBasicColumn({ field: 'description' }, { field: 'description' }),
      generateMultiEntityColumn(
        { field: 'eventType.categories', headerName: 'Categories' },
        { field: 'eventType', tEntity: 'category', mainClass: CCategory },
        {
          inheritedProps,
          chips: { onlyChips: true, limitChips: 5 },
        }
      ),
      generateEntityColumn(
        { field: 'eventType', headerName: 'Event type', isFilterable: true },
        { tEntity: 'eventType', mainClass: CEventType },
        { inheritedProps, generateSearchEntitySelector, onClick: onClickMultiEntityCell }
      ),
      generateMultiEntityColumn(
        { field: 'usersEvent', headerName: 'Users', isFilterable: true },
        { tEntity: 'users', mainClass: CUser },
        { inheritedProps, generateSearchEntitySelector, onClick: onClickMultiEntityCell }
      ),
      generateMultiEntityColumn(
        { field: 'relatedItemsEvents', headerName: 'Related items', isFilterable: true },
        { tEntity: 'items', mainClass: CElement },
        { inheritedProps, generateSearchEntitySelector, onClick: onClickMultiEntityCell }
      ),
      generateEntityColumn(
        { field: 'triggerItemsEvents', headerName: 'Trigger item' },
        { field: 'triggerItem', mainClass: CElement },
        { inheritedProps }
      ),
      generateMultiEntityColumn(
        { field: 'locationEvent', headerName: 'Location', isFilterable: true },
        { tEntity: 'location', mainClass: CLocation },
        {
          inheritedProps,
          generateSearchEntitySelector,
          onClick: onClickMultiEntityCell,
        }
      ),
      ...generateExtraColumnsFromSettings(),
    ];
  }, [props, configComponent]);

  //#endregion

  const customConfig: IConfigComponent = {
    ...configComponent,
    ...(tElement && {
      filtersFetchData: isLocation
        ? {
            locationId: tElement.getID(),
          }
        : {
            itemId: tElement.getID(),
          },
    }),
  };

  return (
    <WrapperDataGrid
      title={props.title}
      configComponent={customConfig}
      dataGridConfig={config}
      columns={getColumns}
      //extraActions={actions}
      enabledFilters
      dataGridMode="server"
      //From grandpa
      inheritedProps={props.inheritedProps}
      infoDataGrid={props.infoDataGrid}
    />
  );
};

export default Events;
